// VerComoProvider — modo "Ver como" del DIRECTIVO (suplantación de solo lectura).
//
// El directivo elige una PERSONA real de su tenant (coordinador, supervisor,
// líder, técnico o coordinador de mtto). A partir de ahí, el backend responde
// EXACTAMENTE como esa persona: mismo scope (RLS por user_id), mismos endpoints
// (pasan los gates de su rol) y mismas capacidades. El FE lo lleva al mundo real
// de ese rol (su home, su sidebar). Toda escritura queda bloqueada en el server.
//
// Mecanismo: el `client.ts` adjunta el header `X-Ver-Como: <targetUserId>` en
// cada request mientras el modo esté activo (lee de sessionStorage, porque corre
// fuera del árbol React). El middleware `requireAuth` re-deriva el contexto.
//
// Estado en Context (patrón AuthProvider; el repo no usa Zustand) + espejo en
// sessionStorage: sobrevive a un refresh y es legible por el client HTTP.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/** A dónde lleva el espejo. Los tres primeros son roles de plataforma; el
 *  cuarto es el módulo de mantenimiento (gate por capability, no por rol). */
export type DestinoVerComo = 'coordinador' | 'supervisor' | 'lider' | 'mantenimiento';

const DESTINOS: readonly DestinoVerComo[] = ['coordinador', 'supervisor', 'lider', 'mantenimiento'];

export interface VerComoState {
  /** Usuario que se está suplantando (viaja en el header X-Ver-Como). */
  targetUserId: string;
  /** Mundo del rol al que se entra. */
  destino: DestinoVerComo;
  /** Nombre completo de la persona (para el banner). */
  nombre: string;
  /** Etiqueta de planta(s) de la persona (para el banner). */
  plantaLabel: string | null;
}

interface VerComoContextValue {
  estado: VerComoState | null;
  entrar: (s: VerComoState) => void;
  salir: () => void;
}

const VerComoContext = createContext<VerComoContextValue | undefined>(undefined);

const STORAGE_KEY = 'jidokap.ver-como';

/** Lee y valida el estado del modo espejo desde sessionStorage (o null). */
export function leerVerComoState(): VerComoState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<VerComoState>;
    if (typeof p.targetUserId !== 'string' || !DESTINOS.includes(p.destino as DestinoVerComo)) {
      return null;
    }
    return {
      targetUserId: p.targetUserId,
      destino: p.destino as DestinoVerComo,
      nombre: typeof p.nombre === 'string' ? p.nombre : '',
      plantaLabel: typeof p.plantaLabel === 'string' ? p.plantaLabel : null,
    };
  } catch {
    return null;
  }
}

/**
 * user_id objetivo del modo espejo, o null. Lo consume `client.ts` (fuera de
 * React) para adjuntar el header `X-Ver-Como` en cada request.
 */
export function verComoTargetId(): string | null {
  return leerVerComoState()?.targetUserId ?? null;
}

/** Home del mundo de un destino de suplantación. */
export function homeVerComo(destino: DestinoVerComo): string {
  switch (destino) {
    case 'coordinador':
      return '/coordinador';
    case 'supervisor':
      return '/supervisor';
    case 'lider':
      return '/capturar';
    case 'mantenimiento':
      return '/mantenimiento';
  }
}

export function VerComoProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<VerComoState | null>(() => leerVerComoState());

  const entrar = useCallback((s: VerComoState) => {
    setEstado(s);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      /* modo incógnito / storage lleno: el modo vive solo en memoria */
    }
  }, []);

  const salir = useCallback(() => {
    setEstado(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* no-op */
    }
  }, []);

  const value = useMemo<VerComoContextValue>(
    () => ({ estado, entrar, salir }),
    [estado, entrar, salir],
  );

  return <VerComoContext.Provider value={value}>{children}</VerComoContext.Provider>;
}

export function useVerComo(): VerComoContextValue {
  const ctx = useContext(VerComoContext);
  if (!ctx) throw new Error('useVerComo debe usarse dentro de <VerComoProvider>');
  return ctx;
}

/** true si hay un modo espejo activo (para ocultar acciones / mostrar banner). */
export function useVerComoActivo(): boolean {
  return useVerComo().estado !== null;
}

/**
 * Rol de plataforma EFECTIVO impuesto por el modo espejo, o null si no hay modo
 * (o el destino es mantenimiento, que no se gatea por rol). Lo usan los guards
 * y sidebars para comportarse como el rol suplantado.
 */
export function useRolEfectivoVerComo(): 'coordinador' | 'supervisor' | 'lider' | null {
  const { estado } = useVerComo();
  if (!estado || estado.destino === 'mantenimiento') return null;
  return estado.destino;
}
